"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import LogoBrand from "@/components/ui/LogoBrand";

const servicesDropdown = [
  {
    label: "Services Overview",
    href: "/services",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5"/>
        <rect x="14" y="3" width="7" height="7" rx="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5"/>
        <rect x="14" y="14" width="7" height="7" rx="1.5"/>
      </svg>
    ),
    desc: "All IT services for growing NZ businesses",
  },
  {
    label: "Infrastructure & Technology",
    href: "/it-infrastructure",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="2" width="18" height="6" rx="2"/>
        <rect x="3" y="10" width="18" height="6" rx="2"/>
        <circle cx="6.5" cy="5" r="1" fill="currentColor" stroke="none"/>
        <circle cx="6.5" cy="13" r="1" fill="currentColor" stroke="none"/>
        <line x1="10" y1="5" x2="17" y2="5"/>
        <line x1="10" y1="13" x2="17" y2="13"/>
        <path d="M8 18v3M12 18v3M16 18v3"/>
        <line x1="6" y1="21" x2="18" y2="21"/>
      </svg>
    ),
    desc: "End-to-end technology deployment & integration",
  },
  {
    label: "Managed IT Services",
    href: "/managed-it-services",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2.5"/>
        <path d="M8 21h8M12 17v4"/>
        <path d="M6 10h3l1.5-3 2 6 1.5-3H18" strokeWidth="1.8"/>
        <circle cx="18" cy="6" r="1.2" fill="currentColor" stroke="none"/>
      </svg>
    ),
    desc: "Proactive monitoring & full IT management",
  },
  {
    label: "Network Solutions",
    href: "/network-solutions",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"/>
        <circle cx="12" cy="3" r="1.5"/>
        <circle cx="20" cy="17" r="1.5"/>
        <circle cx="4" cy="17" r="1.5"/>
        <line x1="12" y1="4.5" x2="12" y2="9"/>
        <line x1="14.6" y1="14" x2="18.8" y2="16"/>
        <line x1="9.4" y1="14" x2="5.2" y2="16"/>
      </svg>
    ),
    desc: "Reliable, high-performance network design",
  },
  {
    label: "Cybersecurity",
    href: "/cyber-security",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L4 5.5v5.5c0 5.25 3.4 10.15 8 11.5 4.6-1.35 8-6.25 8-11.5V5.5L12 2z"/>
        <rect x="9.5" y="11" width="5" height="4" rx="1"/>
        <path d="M10.5 11V9.5a1.5 1.5 0 0 1 3 0V11"/>
        <circle cx="12" cy="13.2" r="0.6" fill="currentColor" stroke="none"/>
      </svg>
    ),
    desc: "Advanced threat protection & compliance",
  },
  {
    label: "Cloud Solutions",
    href: "/cloud-solutions",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6.5 19h11a4.5 4.5 0 0 0 .5-8.97A7 7 0 0 0 4.06 13.5 3.5 3.5 0 0 0 6.5 19z"/>
        <path d="M9.5 13l2-2 2 2" strokeWidth="1.8"/>
        <path d="M13.5 16l-2 2-2-2" strokeWidth="1.8"/>
        <line x1="11.5" y1="11" x2="11.5" y2="16"/>
      </svg>
    ),
    desc: "Scalable cloud infrastructure, migration & management",
  },
  {
    label: "Business Communications",
    href: "/business-communications",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
        <path d="M14.5 2a8.38 8.38 0 0 1 5.5 5.5" strokeWidth="1.6"/>
        <path d="M14.5 5.5A4.5 4.5 0 0 1 18 8.5" strokeWidth="1.6"/>
      </svg>
    ),
    desc: "Unified comms & collaboration platforms",
  },
  {
    label: "AI & Automation Solutions",
    href: "/ai-automation",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a5 5 0 0 0-4.8 3.6A4 4 0 0 0 4 9.5a4 4 0 0 0 1.5 3.1A4.5 4.5 0 0 0 9 17h2"/>
        <path d="M12 2a5 5 0 0 1 4.8 3.6A4 4 0 0 1 20 9.5a4 4 0 0 1-1.5 3.1A4.5 4.5 0 0 1 15 17h-2"/>
        <line x1="12" y1="2" x2="12" y2="22"/>
        <path d="M9 8h6M8 12h8"/>
        <circle cx="9" cy="8" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="15" cy="8" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="8" cy="12" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="12" r="0.8" fill="currentColor" stroke="none"/>
      </svg>
    ),
    desc: "Intelligent automation to drive efficiency",
  },
  {
    label: "ICT Procurement Services",
    href: "/ict-procurement",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 0 1-8 0"/>
        <path d="M9.5 15l2 2 3.5-3.5" strokeWidth="1.8"/>
      </svg>
    ),
    desc: "Strategic sourcing & vendor management",
  },
];

const industriesDropdown = [
  {
    label: "Industries Overview",
    href: "/industries",
    icon: "🏢",
    desc: "Tailored solutions across all sectors",
  },
  {
    label: "Retail",
    href: "/industries/retail",
    icon: "🛍️",
    desc: "Modern retail technology & POS systems",
  },
  {
    label: "Travel & Hospitality",
    href: "/industries/travel",
    icon: "✈️",
    desc: "Connected experiences for travel brands",
  },
  {
    label: "Professional Services",
    href: "/industries/professional-services",
    icon: "💼",
    desc: "IT solutions for service-based firms",
  },
  {
    label: "Construction",
    href: "/industries/construction",
    icon: "🏗️",
    desc: "Field-ready tech for construction teams",
  },
  {
    label: "Healthcare",
    href: "/industries/healthcare",
    icon: "🏥",
    desc: "Secure, compliant healthcare IT systems",
  },
  {
    label: "Property & Real Estate",
    href: "/industries/property-development",
    icon: "🏠",
    desc: "IT for developers, agencies & property firms",
  },
];

const companyDropdown = [
  {
    label: "Company Overview",
    href: "/company",
    icon: "🏢",
    desc: "The team behind your technology",
  },
  {
    label: "About Us",
    href: "/about",
    icon: "🏛️",
    desc: "Learn about Pro IT Solutions, our team and values",
  },
  {
    label: "Case Studies",
    href: "/case-studies",
    icon: "📊",
    desc: "Real results for New Zealand businesses",
  },
  {
    label: "Insights",
    href: "/insights",
    icon: "💡",
    desc: "IT guides, tips and expert advice",
  },
  {
    label: "Careers",
    href: "/careers",
    icon: "🚀",
    desc: "Join our growing Auckland IT team",
  },
];

interface MegaDropdownProps {
  label: string;
  items: { label: string; href: string; icon: React.ReactNode; desc: string }[];
  isActive?: boolean;
  columns?: number;
  overviewHref?: string;
  alignRight?: boolean;
}

function MegaDropdown({ label, items, isActive, columns = 2, overviewHref, alignRight }: MegaDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Filter out overview item for grid display
  const overviewItem = items.find((i) => i.href === overviewHref);
  const gridItems = overviewHref ? items.filter((i) => i.href !== overviewHref) : items;

  const panelWidth = columns === 2 ? "580px" : "320px";

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="relative flex items-center gap-1 px-4 py-2.5 text-[15px] font-body font-500 tracking-wide transition-all duration-200 rounded-lg"
        style={{
          color: open || isActive ? "#F8FAFC" : "rgba(203,213,225,0.8)",
          background: open ? "rgba(255,255,255,0.05)" : "transparent",
        }}
      >
        {label}
        <motion.svg
          className="w-3.5 h-3.5"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
        {isActive && (
          <span
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full"
            style={{ background: "linear-gradient(90deg, #3B82F6, #22D3EE)" }}
          />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-full mt-2 z-50 rounded-2xl overflow-hidden"
            style={{
              ...(alignRight ? { right: 0 } : { left: label === "Services" ? "-120px" : "-60px" }),
              width: panelWidth,
              background: "rgba(7,11,20,0.95)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
              border: "1px solid rgba(59,130,246,0.25)",
              boxShadow:
                "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(59,130,246,0.1), 0 0 40px rgba(59,130,246,0.06)",
            }}
          >
            {/* Top accent line */}
            <div
              className="h-[2px] w-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #3B82F6 30%, #22D3EE 70%, transparent)",
              }}
            />

            <div className="p-4">
              {/* Overview link if present */}
              {overviewItem && (
                <Link
                  href={overviewItem.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 mb-3 rounded-xl transition-all duration-150 group"
                  style={{
                    background: "rgba(59,130,246,0.06)",
                    border: "1px solid rgba(59,130,246,0.12)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background =
                      "rgba(59,130,246,0.12)";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor =
                      "rgba(59,130,246,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background =
                      "rgba(59,130,246,0.06)";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor =
                      "rgba(59,130,246,0.12)";
                  }}
                >
                  <span className="text-lg flex items-center justify-center" style={{ color: "#60A5FA" }}>{overviewItem.icon}</span>
                  <div>
                    <div className="text-[13px] font-heading font-600 text-white">
                      {overviewItem.label}
                    </div>
                    <div className="text-[11.5px] font-body" style={{ color: "rgba(148,163,184,0.7)" }}>
                      {overviewItem.desc}
                    </div>
                  </div>
                  <svg
                    className="ml-auto w-4 h-4 opacity-40"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#3B82F6"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}

              {/* Divider label */}
              {overviewItem && (
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.06)" }} />
                  <span
                    className="text-[10.5px] font-body tracking-widest uppercase"
                    style={{ color: "rgba(148,163,184,0.4)" }}
                  >
                    All {label}
                  </span>
                  <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.06)" }} />
                </div>
              )}

              {/* Grid items */}
              <div
                className="grid gap-1"
                style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
              >
                {gridItems.map((item, i) => (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03, duration: 0.15 }}
                  >
                    <Link
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group relative"
                      style={{ color: "rgba(203,213,225,0.85)" }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget as HTMLAnchorElement;
                        el.style.background = "rgba(59,130,246,0.08)";
                        el.style.borderLeft = "2px solid rgba(59,130,246,0.5)";
                        el.style.paddingLeft = "10px";
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget as HTMLAnchorElement;
                        el.style.background = "transparent";
                        el.style.borderLeft = "2px solid transparent";
                        el.style.paddingLeft = "10px";
                      }}
                      onFocus={(e) => {
                        const el = e.currentTarget as HTMLAnchorElement;
                        el.style.background = "rgba(59,130,246,0.08)";
                      }}
                      onBlur={(e) => {
                        const el = e.currentTarget as HTMLAnchorElement;
                        el.style.background = "transparent";
                      }}
                    >
                      <span
                        className="text-base mt-0.5 shrink-0 w-7 h-7 flex items-center justify-center rounded-lg"
                        style={{ background: "rgba(59,130,246,0.1)", color: "#60A5FA" }}
                      >
                        {item.icon}
                      </span>
                      <div className="min-w-0">
                        <div
                          className="text-[12.5px] font-heading font-600 leading-tight mb-0.5 transition-colors duration-150"
                          style={{ color: "#E2E8F0" }}
                        >
                          {item.label}
                        </div>
                        <div
                          className="text-[11px] font-body leading-snug"
                          style={{ color: "rgba(148,163,184,0.65)" }}
                        >
                          {item.desc}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const servicePaths = ["/services", "/managed-it-services", "/cyber-security", "/cloud-solutions", "/network-solutions", "/business-communications", "/ai-automation", "/ict-procurement", "/it-infrastructure"];
  const isServicesActive = servicePaths.some(p => pathname?.startsWith(p));
  const isIndustriesActive = pathname?.startsWith("/industries");
  const isCompanyActive =
    pathname?.startsWith("/company") ||
    pathname?.startsWith("/about") ||
    pathname?.startsWith("/case-studies") ||
    pathname?.startsWith("/insights") ||
    pathname?.startsWith("/careers");

  const handleDragEnd = (_: unknown, info: { offset: { y: number }; velocity: { y: number } }) => {
    if (info.offset.y > 80 || info.velocity.y > 300) {
      setMobileOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2 border-b" : "bg-transparent py-3"
      }`}
      style={
        scrolled
          ? {
              background: "rgba(7,11,20,0.9)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              borderColor: "rgba(255,255,255,0.08)",
            }
          : {}
      }
    >
      <div className="max-w-container mx-auto px-6 flex items-center justify-between h-[72px]">
        {/* Logo */}
        {/* Mobile: sm, Desktop: md */}
        <span className="lg:hidden"><LogoBrand size="sm" /></span>
        <span className="hidden lg:inline-flex"><LogoBrand size="md" /></span>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {/* Home */}
          <Link
            href="/"
            className="relative px-4 py-2.5 text-[15px] font-body font-500 tracking-wide transition-all duration-200 rounded-lg"
            style={{ color: pathname === "/" ? "#F8FAFC" : "rgba(203,213,225,0.8)" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = "#F8FAFC";
              (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.05)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = pathname === "/" ? "#F8FAFC" : "rgba(203,213,225,0.8)";
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
            }}
          >
            Home
            {pathname === "/" && (
              <span
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full"
                style={{ background: "linear-gradient(90deg, #3B82F6, #22D3EE)" }}
              />
            )}
          </Link>

          <MegaDropdown
            label="Services"
            items={servicesDropdown}
            isActive={isServicesActive}
            columns={2}
            overviewHref="/services"
          />
          <MegaDropdown
            label="Industries"
            items={industriesDropdown}
            isActive={isIndustriesActive}
            columns={2}
            overviewHref="/industries"
          />
          <MegaDropdown
            label="Company"
            items={companyDropdown}
            isActive={isCompanyActive}
            columns={2}
            overviewHref="/company"
            alignRight
          />

          {/* Contact */}
          <Link
            href="/contact"
            className="relative px-4 py-2.5 text-[15px] font-body font-500 tracking-wide transition-all duration-200 rounded-lg"
            style={{ color: pathname === "/contact" ? "#F8FAFC" : "rgba(203,213,225,0.8)" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = "#F8FAFC";
              (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.05)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = pathname === "/contact" ? "#F8FAFC" : "rgba(203,213,225,0.8)";
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
            }}
          >
            Contact
            {pathname === "/contact" && (
              <span
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full"
                style={{ background: "linear-gradient(90deg, #3B82F6, #22D3EE)" }}
              />
            )}
          </Link>
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/contact"
            className="px-6 py-3 text-[14px] font-heading font-500 rounded-pill transition-all duration-200 btn-secondary"
          >
            Request a Quote
          </Link>
          <Link
            href="/book"
            className="px-6 py-3 text-[14px] font-heading font-600 rounded-pill btn-primary"
          >
            Book a Strategy Call
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden flex flex-col gap-1.5 p-3 rounded-lg transition-colors min-w-[44px] min-h-[44px] items-center justify-center"
          style={{ background: "rgba(255,255,255,0.05)" }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle mobile menu"
        >
          <motion.span
            className="block w-5 h-0.5 bg-white origin-center"
            animate={{ rotate: mobileOpen ? 45 : 0, y: mobileOpen ? 8 : 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.span
            className="block w-5 h-0.5 bg-white"
            animate={{ opacity: mobileOpen ? 0 : 1 }}
            transition={{ duration: 0.2 }}
          />
          <motion.span
            className="block w-5 h-0.5 bg-white origin-center"
            animate={{ rotate: mobileOpen ? -45 : 0, y: mobileOpen ? -8 : 0 }}
            transition={{ duration: 0.2 }}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.3 }}
            onDragEnd={handleDragEnd}
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden border-t"
            style={{
              background: "rgba(7,11,20,0.97)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              borderColor: "rgba(255,255,255,0.08)",
              cursor: "grab",
              maxHeight: "calc(100dvh - 84px)",
              overflowY: "auto",
            }}
          >
            {/* Drag handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div
                className="w-10 h-1 rounded-full"
                style={{ background: "rgba(148,163,184,0.35)" }}
              />
            </div>

            <div className="max-w-container mx-auto px-6 py-4 flex flex-col gap-1">
              {/* Home */}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0, duration: 0.2 }}
              >
                <Link
                  href="/"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 text-[15px] font-body font-medium transition-colors rounded-lg hover:bg-white/5"
                  style={{ color: pathname === "/" ? "#60A5FA" : "rgba(203,213,225,0.8)" }}
                >
                  Home
                </Link>
              </motion.div>

              {/* Mobile Services Accordion */}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05, duration: 0.2 }}
              >
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 text-[15px] font-body font-medium rounded-lg hover:bg-white/5 active:bg-white/10 transition-colors"
                  style={{ color: isServicesActive ? "#60A5FA" : "rgba(203,213,225,0.8)" }}
                >
                  Services
                  <motion.svg
                    animate={{ rotate: mobileServicesOpen ? 180 : 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </button>
                <AnimatePresence initial={false}>
                  {mobileServicesOpen && (
                    <motion.div
                      key="services-panel"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.28, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-1 pt-1 pb-2 pl-2">
                        {servicesDropdown.map((item, i) => (
                          <motion.div
                            key={item.href}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04, duration: 0.15 }}
                          >
                            <Link
                              href={item.href}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-start gap-3 px-3 py-3 rounded-xl transition-all duration-150 active:bg-blue-500/10 group"
                              style={{
                                color: pathname === item.href ? "#60A5FA" : "rgba(203,213,225,0.85)",
                                borderLeft: pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent",
                              }}
                              onTouchStart={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "rgba(59,130,246,0.08)";
                                el.style.borderLeft = "2px solid rgba(59,130,246,0.6)";
                              }}
                              onTouchEnd={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "transparent";
                                el.style.borderLeft = pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent";
                              }}
                              onMouseEnter={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "rgba(59,130,246,0.08)";
                                el.style.borderLeft = "2px solid rgba(59,130,246,0.6)";
                              }}
                              onMouseLeave={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "transparent";
                                el.style.borderLeft = pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent";
                              }}
                            >
                              <span
                                className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-base mt-0.5"
                                style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}
                              >
                                {item.icon}
                              </span>
                              <div className="min-w-0">
                                <div
                                  className="text-[13.5px] font-heading font-600 leading-tight mb-0.5"
                                  style={{ color: pathname === item.href ? "#60A5FA" : "#E2E8F0" }}
                                >
                                  {item.label}
                                </div>
                                <div
                                  className="text-[11.5px] font-body leading-snug"
                                  style={{ color: "rgba(148,163,184,0.65)" }}
                                >
                                  {item.desc}
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Mobile Industries Accordion */}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.2 }}
              >
                <button
                  onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 text-[15px] font-body font-medium rounded-lg hover:bg-white/5 active:bg-white/10 transition-colors"
                  style={{ color: isIndustriesActive ? "#60A5FA" : "rgba(203,213,225,0.8)" }}
                >
                  Industries
                  <motion.svg
                    animate={{ rotate: mobileIndustriesOpen ? 180 : 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </button>
                <AnimatePresence initial={false}>
                  {mobileIndustriesOpen && (
                    <motion.div
                      key="industries-panel"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.28, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-1 pt-1 pb-2 pl-2">
                        {industriesDropdown.map((item, i) => (
                          <motion.div
                            key={item.href}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04, duration: 0.15 }}
                          >
                            <Link
                              href={item.href}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-start gap-3 px-3 py-3 rounded-xl transition-all duration-150 active:bg-blue-500/10 group"
                              style={{
                                color: pathname === item.href ? "#60A5FA" : "rgba(203,213,225,0.85)",
                                borderLeft: pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent",
                              }}
                              onTouchStart={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "rgba(59,130,246,0.08)";
                                el.style.borderLeft = "2px solid rgba(59,130,246,0.6)";
                              }}
                              onTouchEnd={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "transparent";
                                el.style.borderLeft = pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent";
                              }}
                              onMouseEnter={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "rgba(59,130,246,0.08)";
                                el.style.borderLeft = "2px solid rgba(59,130,246,0.6)";
                              }}
                              onMouseLeave={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "transparent";
                                el.style.borderLeft = pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent";
                              }}
                            >
                              <span
                                className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-base mt-0.5"
                                style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}
                              >
                                {item.icon}
                              </span>
                              <div className="min-w-0">
                                <div
                                  className="text-[13.5px] font-heading font-600 leading-tight mb-0.5"
                                  style={{ color: pathname === item.href ? "#60A5FA" : "#E2E8F0" }}
                                >
                                  {item.label}
                                </div>
                                <div
                                  className="text-[11.5px] font-body leading-snug"
                                  style={{ color: "rgba(148,163,184,0.65)" }}
                                >
                                  {item.desc}
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Mobile Company Accordion */}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.2 }}
              >
                <button
                  onClick={() => setMobileCompanyOpen(!mobileCompanyOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 text-[15px] font-body font-medium rounded-lg hover:bg-white/5 active:bg-white/10 transition-colors"
                  style={{ color: isCompanyActive ? "#60A5FA" : "rgba(203,213,225,0.8)" }}
                >
                  Company
                  <motion.svg
                    animate={{ rotate: mobileCompanyOpen ? 180 : 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </button>
                <AnimatePresence initial={false}>
                  {mobileCompanyOpen && (
                    <motion.div
                      key="company-panel"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.28, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-1 pt-1 pb-2 pl-2">
                        {companyDropdown.map((item, i) => (
                          <motion.div
                            key={item.href}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04, duration: 0.15 }}
                          >
                            <Link
                              href={item.href}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-start gap-3 px-3 py-3 rounded-xl transition-all duration-150 active:bg-blue-500/10 group"
                              style={{
                                color: pathname === item.href ? "#60A5FA" : "rgba(203,213,225,0.85)",
                                borderLeft: pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent",
                              }}
                              onTouchStart={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "rgba(59,130,246,0.08)";
                                el.style.borderLeft = "2px solid rgba(59,130,246,0.6)";
                              }}
                              onTouchEnd={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "transparent";
                                el.style.borderLeft = pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent";
                              }}
                              onMouseEnter={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "rgba(59,130,246,0.08)";
                                el.style.borderLeft = "2px solid rgba(59,130,246,0.6)";
                              }}
                              onMouseLeave={(e) => {
                                const el = e.currentTarget as HTMLAnchorElement;
                                el.style.background = "transparent";
                                el.style.borderLeft = pathname === item.href ? "2px solid rgba(59,130,246,0.7)" : "2px solid transparent";
                              }}
                            >
                              <span
                                className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-base mt-0.5"
                                style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}
                              >
                                {item.icon}
                              </span>
                              <div className="min-w-0">
                                <div
                                  className="text-[13.5px] font-heading font-600 leading-tight mb-0.5"
                                  style={{ color: pathname === item.href ? "#60A5FA" : "#E2E8F0" }}
                                >
                                  {item.label}
                                </div>
                                <div
                                  className="text-[11.5px] font-body leading-snug"
                                  style={{ color: "rgba(148,163,184,0.65)" }}
                                >
                                  {item.desc}
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Contact */}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.2 }}
              >
                <Link
                  href="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 text-[15px] font-body font-medium transition-colors rounded-lg hover:bg-white/5"
                  style={{ color: pathname === "/contact" ? "#60A5FA" : "rgba(203,213,225,0.8)" }}
                >
                  Contact
                </Link>
              </motion.div>

              <motion.div
                className="pt-4 flex flex-col gap-2"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.2 }}
              >
                <Link
                  href="/book"
                  onClick={() => setMobileOpen(false)}
                  className="px-5 py-3 text-[14px] font-heading font-600 rounded-xl text-center btn-primary"
                >
                  Book a Strategy Call
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}